import { ActionTree } from 'vuex';

import { TrackingHelper } from '../helpers/tracking.helper';
import { IRootState } from '@signing-page/store/store';
import { ITrackingState } from '@signing-page/tracking/store/tracking.store';
import {
  mutationSetCurrentDocumentPages,
  mutationSetPageViewStopWatchPaused,
  mutationSetSuspendPageTracking,
  mutationSetTrackingDisabled,
} from '@signing-page/tracking/store/tracking.mutations';

import TrackingService from '@signing-page/api/tracking/services/tracking.service';
import { IDocumentPage } from '@lib-shared/types/document-page';
import { ERecipientStatus } from '@signing-page/recipient/types/enums/recipient-status';

export const actionSetCurrentDocumentPages = 'setCurrentDocumentPages';
export const actionTrackFieldClick = 'trackFieldClick';
export const actionTrackVideo = 'trackVideo';
export const actionTrackVisitTime = 'trackVisitTime';
export const actionTrackContentLinkClick = 'trackContentLinkBlockClick';

export const actionStartPageViewStopWatch = 'startPageViewStopWatch';
export const actionResetPageViewStopWatch = 'resetPageViewStopWatch';
export const actionPausePageViewStopWatch = 'pausePageViewStopWatch';
export const actionResumePageViewStopWatch = 'resumePageViewStopWatch';

export const actionSuspendPageTracking = 'suspendPageTracking';
export const actionResumePageTracking = 'resumePageTracking';

export const actionEnableTracking = 'enableTracking';
export const actionDisableTracking = 'disableTracking';

export const actions: ActionTree<ITrackingState, IRootState> = {
  [actionSetCurrentDocumentPages]({ commit }, pages: IDocumentPage[]) {
    commit(mutationSetCurrentDocumentPages, pages);
  },
  async [actionTrackFieldClick]({ state }, id: string) {
    if (state.trackingDisabled) {
      return;
    }
    try {
      await TrackingService.trackFieldClick(id);
    } catch (_) {
      console.log(`Failed to track click for field with id ${id}`);
    }
  },
  async [actionTrackVisitTime](
    { state, rootState, dispatch },
    { pages, pageTime }: { pages: IDocumentPage[]; pageTime: number }
  ) {
    const minValueAllowed = 100;
    if (state.trackingDisabled || state.suspendPageTracking || pageTime < minValueAllowed) {
      return;
    }
    try {
      if (!pages) {
        return;
      }
      pages.forEach(async (page: IDocumentPage) => {
        try {
          await TrackingService.trackVisitTime(page.pageNum, pageTime);
        } catch (e) {
          console.log(e);
        }
      });
      const sendPush: boolean = TrackingHelper.shouldSendViewedPush(
        rootState.document.document.recipients,
        rootState.recipientId
      );
      if (sendPush) {
        dispatch(
          'document/setRecipientStatus',
          { status: ERecipientStatus.Viewed },
          { root: true }
        );
      }
    } catch (_) {
      console.log(`Failed to track visit time for pages`);
    }
  },
  async [actionTrackContentLinkClick]({ state }, id: string) {
    if (state.trackingDisabled) {
      return;
    }
    try {
      await TrackingService.trackContentLinkBlockClick(id);
    } catch (_) {
      console.log(`Failed to track click for content-link with id ${id}`);
    }
  },
  async [actionTrackVideo]({ state }, { id, viewDuration }: { id: string; viewDuration: number }) {
    if (state.trackingDisabled) {
      return;
    }
    try {
      await TrackingService.trackVideo(id, viewDuration);
    } catch (_) {
      console.log(`Failed to track video with id ${id}`);
    }
  },
  [actionStartPageViewStopWatch]({ commit, state }) {
    if (state.trackingDisabled || state.suspendPageTracking || !state.currentDocumentPages) {
      return;
    }
    try {
      state.pageViewStopWatch.start();
      commit(mutationSetPageViewStopWatchPaused, false);
    } catch (error) {
      return error;
    }
  },
  [actionResetPageViewStopWatch]({ commit, dispatch, state }, pages: IDocumentPage[]) {
    if (state.trackingDisabled) {
      return;
    }
    try {
      const pageTime: number = state.pageViewStopWatch.stop();
      commit(mutationSetPageViewStopWatchPaused, true);
      if (pages.length > 0) {
        dispatch(actionTrackVisitTime, { pages, pageTime });
      }
    } catch (error) {
      console.log(error);
    }
  },
  [actionPausePageViewStopWatch]({ commit, state }) {
    if (state.trackingDisabled || state.suspendPageTracking) {
      return;
    }
    try {
      if (state.currentDocumentPages && !state.pageViewStopWatchPaused) {
        state.pageViewStopWatch.pause();
        commit(mutationSetPageViewStopWatchPaused, true);
      }
    } catch (error) {
      console.log(error);
    }
  },
  [actionResumePageViewStopWatch]({ commit, state }) {
    if (state.trackingDisabled || state.suspendPageTracking) {
      return;
    }
    try {
      if (state.currentDocumentPages && state.pageViewStopWatchPaused) {
        state.pageViewStopWatch.resume();
        commit(mutationSetPageViewStopWatchPaused, false);
      }
    } catch (error) {
      console.log(error);
    }
  },
  [actionSuspendPageTracking]({ commit, dispatch, state }) {
    if (!state.currentDocumentPages) {
      return;
    }
    state.currentDocumentPages.forEach((page: IDocumentPage) => {
      dispatch(actionResetPageViewStopWatch, page);
    });
    commit(mutationSetSuspendPageTracking, true);
  },
  [actionResumePageTracking]({ commit, dispatch }) {
    commit(mutationSetSuspendPageTracking, false);
    dispatch(actionStartPageViewStopWatch);
  },
  [actionEnableTracking]({ commit }) {
    commit(mutationSetTrackingDisabled, false);
  },
  [actionDisableTracking]({ commit }) {
    commit(mutationSetTrackingDisabled, true);
  },
};
